import React from 'react';
import './Help.css';

const Help = () => {
  return (
    <div className="help-container">
      <div className="help-content">
        <h1>MÅSTE Research Library - User Manual</h1>

        <div className="toc">
          <h2>Table of Contents</h2>
          <ol>
            <li><a href="#getting-started">Getting Started</a></li>
            <li><a href="#browsing-documents">Browsing Documents</a></li>
            <li><a href="#uploading-files">Uploading Files</a></li>
            <li><a href="#managing-folders">Managing Folders</a></li>
            <li><a href="#searching-filtering">Searching & Filtering</a></li>
            <li><a href="#tips-best-practices">Tips & Best Practices</a></li>
          </ol>
        </div>

        <hr />

        <h2 id="getting-started">Getting Started</h2>

        <h3>What is this Research Library?</h3>
        <p>This is a collaborative document management system designed for research teams. You can upload documents, organize them into folders, and share materials with your team or the public.</p>

        <h3>Accessing the System</h3>
        <ul>
          <li>Log in using your authorized account credentials</li>
          <li>Your name will automatically be associated with any files or folders you create</li>
        </ul>

        <hr />

        <h2 id="browsing-documents">Browsing Documents</h2>

        <h3>Main Database ("Search All Files")</h3>
        <p>This is your primary view of all documents in the system.</p>

        <p><strong>What you'll see:</strong></p>
        <ul>
          <li>A list of all folders and individual files you have access to</li>
          <li>File information: Title, Author, Upload Date, File Type, and Country</li>
        </ul>

        <p><strong>How to open a file:</strong></p>
        <ul>
          <li>Simply <strong>click on any file</strong> in the list to open or download it</li>
          <li>PDFs, images, and Office documents can be viewed in your browser</li>
          <li>Other file types will download automatically</li>
        </ul>

        <p><strong>How to open a folder:</strong></p>
        <ul>
          <li>Click the <strong>folder icon</strong> (📁) to expand the folder and see its contents</li>
          <li>A window will appear showing all files and subfolders inside</li>
          <li>Click on any file to download it</li>
          <li>Click the folder icon again to close the folder view</li>
        </ul>

        <h3>Public Documents</h3>
        <p>This section shows only documents marked as "Public" - materials that can be shared outside your team.</p>
        <p><strong>Works the same as the main database but only shows public files</strong></p>

        <h3>Quick Filter Tabs in the Documents Menu</h3>
        <p>The <strong>DOCUMENTS</strong> dropdown menu contains quick filter options to help you find specific types of files instantly. These are pre-filtered views based on tags:</p>

        <div className="tip">
          <p><strong>Workpackage Filters (WP1-WP6):</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li><strong>WP1, WP2, WP3, WP4, WP5, WP6</strong> - Each tab shows only files and folders tagged with that specific workpackage</li>
            <li>Use these to quickly access workpackage-specific materials</li>
            <li>Files must be tagged with a workpackage during upload to appear here</li>
          </ul>
        </div>

        <div className="tip">
          <p><strong>Category Filters:</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li><strong>Contact Lists</strong> - Shows only files/folders tagged as "Contact List"</li>
            <li><strong>Deliverables</strong> - Shows only files/folders tagged as "Deliverable"</li>
            <li><strong>Meetings</strong> - Shows only files/folders tagged as "Meeting"</li>
            <li><strong>Promotion materials</strong> - Shows only files/folders tagged as "Promotion"</li>
            <li><strong>Publications</strong> - Shows only files/folders tagged as "Publication"</li>
            <li><strong>Reports</strong> - Shows only files/folders tagged as "Report"</li>
            <li><strong>Templates</strong> - Shows only files/folders tagged as "Template"</li>
          </ul>
        </div>

        <div className="note">
          <p><strong>How Quick Filters Work:</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li>Each tab displays files and folders that match the specific tag</li>
            <li>Files appear in these views based on the tags you selected during upload</li>
            <li>A single file can appear in multiple tabs if it has multiple tags</li>
            <li>Folders with shared access may also appear with quick access to their contents</li>
            <li>To tag files correctly, use the checkboxes on the Upload page</li>
          </ul>
        </div>

        <hr />

        <h2 id="uploading-files">Uploading Files</h2>

        <p>There are <strong>two ways</strong> to upload files:</p>

        <h3>Option 1: Create a New Folder with Files</h3>

        <p>Use this when you want to organize related files together.</p>

        <div className="steps">
          <p><strong>Steps:</strong></p>
          <ol>
            <li>Go to the <strong>Upload</strong> page</li>
            <li>Click "Click to choose files" to select your documents</li>
            <li><strong>Enter a Folder Name</strong> (e.g., "Q1 2024 Reports")</li>
            <li>(Optional) Select a <strong>Parent Folder</strong> if this should be a subfolder</li>
            <li>(Optional) Check <strong>"Allow editors and admins to upload files to this folder"</strong> for collaborative folders</li>
            <li>Select <strong>Workpackage</strong> if relevant (WP1-WP6)</li>
            <li>Check any relevant tags:
              <ul>
                <li><strong>Public Document</strong> - visible to everyone</li>
                <li><strong>Meeting</strong> - meeting materials</li>
                <li><strong>Deliverable</strong> - project deliverable</li>
                <li><strong>Contact List</strong> - contact information</li>
                <li><strong>Promotion</strong> - promotional materials</li>
                <li><strong>Report</strong> - formal report</li>
                <li><strong>Publication</strong> - published work</li>
                <li><strong>Template</strong> - reusable template</li>
              </ul>
            </li>
            <li>Fill in <strong>Title</strong>, <strong>Country</strong> (if applicable)</li>
            <li>Click <strong>Upload</strong></li>
          </ol>
        </div>

        <div className="warning">
          <strong>Important:</strong> If you mark something as "Public," you'll get a confirmation prompt!
        </div>

        <h3>Option 2: Upload to an Existing Folder</h3>

        <p>Use this when you want to add files to a folder that already exists.</p>

        <div className="steps">
          <p><strong>Steps:</strong></p>
          <ol>
            <li>On the <strong>Upload</strong> page, scroll to the right side "Upload to Existing Folder"</li>
            <li>Click "Click to choose files" to select your documents</li>
            <li>Select the destination folder from the dropdown
              <ul>
                <li>Folders are shown with indentation to show the hierarchy</li>
                <li>📁 icons help you identify folders</li>
              </ul>
            </li>
            <li>Click <strong>Upload to Folder</strong></li>
          </ol>
        </div>

        <div className="permission-box">
          <strong>Permission Note:</strong> You can only upload to folders if:
          <ul style={{ margin: '5px 0' }}>
            <li>You created the folder, OR</li>
            <li>The folder creator enabled "shared access"</li>
          </ul>
        </div>

        <hr />

        <h2 id="managing-folders">Managing Folders</h2>

        <h3>Creating Folders</h3>

        <p><strong>Main Folders:</strong></p>
        <ul>
          <li>Use the Upload page and provide a "Folder Name"</li>
          <li>Leave "Parent Folder" empty to create a top-level folder</li>
        </ul>

        <p><strong>Subfolders (folders inside folders):</strong></p>
        <ol>
          <li>Open a folder by clicking its icon</li>
          <li>Click the <strong>"+ Subfolder"</strong> button</li>
          <li>Enter the subfolder name</li>
          <li>Click <strong>Create</strong></li>
        </ol>

        <h3>Organizing Files with Drag & Drop</h3>

        <p>You can move files and folders by dragging them!</p>

        <p><strong>Moving Files:</strong></p>
        <ol>
          <li>Open a folder to see its contents</li>
          <li><strong>Click and drag</strong> any file with the ☰ grab icon</li>
          <li>Drop it into another folder</li>
          <li><strong>Permission required:</strong> Only the folder creator can rearrange files</li>
        </ol>

        <p><strong>Moving Folders:</strong></p>
        <ol>
          <li>In the main list, <strong>click and drag</strong> any folder you created</li>
          <li>Drop it onto another folder to make it a subfolder</li>
          <li>Drop it in empty space to move it to the top level</li>
          <li><strong>You can only move folders you created</strong></li>
        </ol>

        <div className="tip">
          <strong>Visual Feedback:</strong>
          <ul style={{ margin: '5px 0' }}>
            <li>While dragging, the target area will highlight in green</li>
            <li>If you see a dashed green border, you can drop there</li>
            <li>Semi-transparent items mean they're being dragged</li>
          </ul>
        </div>

        <h3>Renaming Folders</h3>

        <p><strong>Main list:</strong></p>
        <ul>
          <li>Click the <strong>✏️ pencil icon</strong> next to your folder name</li>
          <li>Enter the new name</li>
          <li>Click <strong>Rename</strong></li>
        </ul>

        <p><strong>Inside opened folders:</strong></p>
        <ul>
          <li>Click the <strong>✏️ Rename</strong> button</li>
          <li>Enter the new name</li>
          <li>Click <strong>Rename</strong></li>
        </ul>

        <div className="permission-box">
          <strong>Permission:</strong> You can only rename folders you created
        </div>

        <h3>Deleting Files & Folders</h3>

        <p><strong>To delete:</strong></p>
        <ul>
          <li>Click the <strong>🗑️ trash icon</strong> next to the item</li>
          <li>Confirm the deletion</li>
        </ul>

        <div className="warning">
          <strong>Important:</strong>
          <ul style={{ margin: '5px 0' }}>
            <li>Deleting a folder <strong>deletes everything inside</strong> (all subfolders and files)</li>
            <li>You can only delete items you created</li>
            <li><strong>This cannot be undone!</strong></li>
          </ul>
        </div>

        <h3>Downloading Folders</h3>

        <p>To download an entire folder as a ZIP file:</p>
        <ol>
          <li>Click the <strong>download icon</strong> (⬇️) next to the folder name</li>
          <li>Confirm the download</li>
          <li>The folder will be packaged as a .zip file with all contents</li>
        </ol>

        <hr />

        <h2 id="searching-filtering">Searching & Filtering</h2>

        <h3>Search Bar</h3>
        <p>Located at the top of the document list.</p>

        <p><strong>You can search by:</strong></p>
        <ul>
          <li><strong>Title</strong> - document or folder name</li>
          <li><strong>Author</strong> - who created it</li>
          <li><strong>Country</strong> - associated country</li>
        </ul>

        <p>Just type in the search box and results filter automatically!</p>

        <h3>Date Range Filter</h3>

        <p>Use the <strong>Start Date</strong> and <strong>End Date</strong> fields to filter by upload date.</p>

        <div className="note">
          <strong>Example:</strong>
          <ul style={{ margin: '5px 0' }}>
            <li>Start Date: 2024-01-01</li>
            <li>End Date: 2024-03-31</li>
            <li>Shows only files uploaded in Q1 2024</li>
          </ul>
        </div>

        <h3>Pagination</h3>

        <p>The list shows <strong>20 items per page</strong>. Use the <strong>Previous</strong> and <strong>Next</strong> buttons at the bottom to navigate through pages.</p>

        <hr />

        <h2 id="tips-best-practices">Tips & Best Practices</h2>

        <h3>For Better Organization</h3>

        <div className="tip">
          <p>✅ <strong>Use descriptive folder names</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li>❌ Bad: "Files", "Stuff", "Misc"</li>
            <li>✅ Good: "Q1 2024 Meeting Notes", "Sweden Contact Lists"</li>
          </ul>
        </div>

        <div className="tip">
          <p>✅ <strong>Use subfolders for related materials</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li>Example: "2024 Reports" &gt; "Q1" &gt; "Country-specific"</li>
          </ul>
        </div>

        <div className="tip">
          <p>✅ <strong>Use tags consistently</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li>Always tag deliverables, meetings, publications</li>
            <li>Makes filtering much easier</li>
          </ul>
        </div>

        <div className="warning">
          <p>✅ <strong>Be cautious with "Public"</strong></p>
          <ul style={{ margin: '5px 0' }}>
            <li>Double-check before marking documents as public</li>
            <li>You'll get a confirmation prompt for safety</li>
          </ul>
        </div>

        <h3>Understanding Permissions</h3>

        <p><strong>You can always:</strong></p>
        <ul>
          <li>View all files you have access to</li>
          <li>Upload files to your own folders</li>
          <li>Upload files to shared-access folders</li>
          <li>Delete your own files and folders</li>
        </ul>

        <p><strong>You cannot:</strong></p>
        <ul>
          <li>Delete someone else's folders or files</li>
          <li>Move folders you didn't create</li>
          <li>Rearrange files in folders you didn't create</li>
        </ul>

        <h3>File Types Supported</h3>

        <p><strong>Documents:</strong></p>
        <ul>
          <li>PDF (.pdf)</li>
          <li>PowerPoint (.ppt, .pptx)</li>
          <li>Word (.doc, .docx)</li>
          <li>Excel (.xls, .xlsx, .csv)</li>
          <li>Text (.txt)</li>
        </ul>

        <p><strong>Images:</strong></p>
        <ul>
          <li>JPEG (.jpg, .jpeg)</li>
          <li>PNG (.png)</li>
        </ul>

        <h3>Common Issues</h3>

        <div className="note">
          <p><strong>Problem: "I can't upload to this folder"</strong></p>
          <p><strong>Solution:</strong> The folder creator didn't enable shared access. Upload to your own folder or ask the creator for access.</p>
        </div>

        <div className="note">
          <p><strong>Problem: "I can't move this file"</strong></p>
          <p><strong>Solution:</strong> Only the folder owner can rearrange files. Contact the folder creator or move the file by uploading it to a different folder.</p>
        </div>

        <div className="note">
          <p><strong>Problem: "The folder view won't close"</strong></p>
          <p><strong>Solution:</strong> Click the folder icon (📁) again to close the expanded view.</p>
        </div>

        <div className="note">
          <p><strong>Problem: "My file name has strange characters"</strong></p>
          <p><strong>Solution:</strong> The system handles special characters (Å, Ä, Ö). If you see issues, try renaming the file before upload.</p>
        </div>

        <hr />

        <h2>Quick Reference</h2>

        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>How To</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Open a file</td>
              <td>Click on the file name</td>
            </tr>
            <tr>
              <td>View folder contents</td>
              <td>Click the 📁 folder icon</td>
            </tr>
            <tr>
              <td>Create folder</td>
              <td>Upload page → Enter "Folder Name"</td>
            </tr>
            <tr>
              <td>Create subfolder</td>
              <td>Open folder → "+ Subfolder" button</td>
            </tr>
            <tr>
              <td>Upload to existing folder</td>
              <td>Upload page → "Upload to Existing Folder" section</td>
            </tr>
            <tr>
              <td>Move files</td>
              <td>Drag file with ☰ icon to another folder</td>
            </tr>
            <tr>
              <td>Rename folder</td>
              <td>Click ✏️ pencil icon</td>
            </tr>
            <tr>
              <td>Delete item</td>
              <td>Click 🗑️ trash icon</td>
            </tr>
            <tr>
              <td>Download folder</td>
              <td>Click ⬇️ download icon</td>
            </tr>
            <tr>
              <td>Search</td>
              <td>Type in search bar at top</td>
            </tr>
            <tr>
              <td>Filter by date</td>
              <td>Use "Start Date" and "End Date" fields</td>
            </tr>
          </tbody>
        </table>

        <hr />

        <h2>Need Help?</h2>

        <p>If you're still having trouble:</p>
        <ol>
          <li>Check if you have the right permissions</li>
          <li>Verify you're logged in with your account</li>
          <li>Try refreshing the page</li>
          <li>Contact your system administrator</li>
        </ol>

        <div className="tip">
          <strong>Remember:</strong> The system is designed to help your team collaborate while protecting sensitive information. When in doubt about making something public, keep it private!
        </div>
      </div>
    </div>
  );
};

export default Help;
